// kbService — thin client for the JVISTA Knowledge Base API (Session C).
//
// Wraps the backend /kb/* endpoints (Parameter Resolver API, Session B).
// Every call fails soft: on any network/HTTP error it logs and returns an
// empty result of the right shape, so callers (kbContextBuilder, the AI
// panel badge) never crash and the chat degrades gracefully to "KB offline".

import axios from 'axios';

// Same env convention as the rest of the app, but the KB router is mounted
// at /kb (NOT under /api/v1).
const KB_BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/kb`;

const TIMEOUT_MS = 15000;

async function _get(path, params, fallback) {
  try {
    const { data } = await axios.get(`${KB_BASE}${path}`, {
      params,
      timeout: TIMEOUT_MS,
    });
    return data;
  } catch (err) {
    // Soft-fail: KB is an enhancement, never a hard dependency for the UI.
    console.warn(`[kbService] ${path} failed:`, err?.message || err);
    return fallback;
  }
}

/**
 * Resolve validated parameters for a material.
 * @returns {{parameters: Object, count: number, technology_filter_applied?: boolean}}
 */
export async function getParameters(material, technology, morphology) {
  if (!material) return { parameters: {}, count: 0 };
  const params = { material };
  if (technology) params.technology = technology;
  if (morphology) params.morphology = morphology;
  return _get('/parameters', params, { parameters: {}, count: 0 });
}

/** @returns {Array} defect rows for a material host. */
export async function getDefects(material) {
  if (!material) return [];
  const data = await _get('/defects', { material }, { results: [] });
  return data.results || [];
}

/** @returns {Array} phenomena rows (filtered by technology and/or keyword). */
export async function getPhenomena(technology, keyword) {
  const params = {};
  if (technology) params.technology = technology;
  if (keyword) params.keyword = keyword;
  if (!params.technology && !params.keyword) return [];
  const data = await _get('/phenomena', params, { results: [] });
  return data.results || [];
}

/**
 * @returns {{results: Array, warnings: Array, count: number}}
 *   DD validity assessment + derived warnings for a technology.
 */
export async function getValidity(technology) {
  if (!technology) return { results: [], warnings: [], count: 0 };
  return _get('/validity', { technology }, { results: [], warnings: [], count: 0 });
}

/**
 * Free-text search of theory references + physics models.
 * @returns {{references: Array, models: Array}}
 */
export async function searchKB(query) {
  if (!query) return { references: [], models: [] };
  return _get('/search', { q: query }, { references: [], models: [] });
}

/** @returns {Array} historical Voc/Jsc/FF/PCE rows, sorted by year. */
export async function getEfficiencyHistory(technology) {
  if (!technology) return [];
  const data = await _get('/efficiency', { technology }, { results: [] });
  return data.results || [];
}

/**
 * KB reachability + row counts (drives the panel badge).
 * @returns {{ok: boolean, total_rows: number, tables: Object}}
 */
export async function getHealth() {
  try {
    const { data } = await axios.get(`${KB_BASE}/health`, { timeout: 8000 });
    return { ok: true, total_rows: data.total_rows || 0, tables: data.tables || {} };
  } catch (err) {
    console.warn('[kbService] health check failed:', err?.message || err);
    return { ok: false, total_rows: 0, tables: {} };
  }
}

const kbService = {
  getParameters,
  getDefects,
  getPhenomena,
  getValidity,
  searchKB,
  getEfficiencyHistory,
  getHealth,
};
export default kbService;
